import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Upload, Typography } from 'antd';
import { beforeUpload } from './UploadHelper';
import { useTranslation } from 'react-i18next';
import classes from './styles.module.scss';
import { useJobService } from '../../services/jobService';
import globalNotification from '../../common/hooks/globalNotification';
import { notificationType } from '../../common/constants/notificationType';
import { FileItem } from './DNDHelper';
import { colors } from '../../theming';

const { Dragger } = Upload;
const { Title } = Typography;

function DragAndDropButton({
    title,
    fileTypes,
    isMultiple,
    files,
    onFileSelect,
    deleteFile,
    isRequired,
    currentFile,
    jobStatus,
}) {
    const { t } = useTranslation(['fileActions', 'jobPreview']);
    const { getFile } = useJobService();
    const [selectedFile, setSelectedFile] = useState(null);
    const [processingFileId, setProcessingFileId] = useState(null);

    useEffect(() => {
        if (!currentFile) {
            setSelectedFile(null);
        }
    }, [currentFile]);

    const props = {
        name: 'file',
        multiple: isMultiple,
        type: 'drag',
        accept: ['application/pdf'],
        maxCount: isMultiple ? 5 : 1,
        className: classes['upload-dragger'],
        showUploadList: {
            showRemoveIcon: false,
            showPreviewIcon: false,
            showDownloadIcon: false,
        },
    };

    const fileList = useMemo(() => {
        const selectedFileList = selectedFile
            ? [
                  {
                      uid: 'selected',
                      name: selectedFile.name,
                      status: 'selected',
                  },
              ]
            : [];

        const uploadedFileList =
            files?.map(file => ({
                uid: file.id || file.fileName,
                name: file.fileName,
                status: 'done',
                isProcessing: processingFileId === file.fileName,
            })) || [];

        return [...selectedFileList, ...uploadedFileList];
    }, [selectedFile, files, processingFileId]);

    // #region Handlers
    // next handlers use useCallback to prevent unnecessary re-renders
    const handlePreview = useCallback(
        async fileName => {
            try {
                setProcessingFileId(fileName);
                const data = await getFile(fileName);
                const url = data.config.baseURL + data.config.url;
                window.open(url, '_blank');
            } catch (error) {
                globalNotification.open({
                    type: notificationType.ERROR,
                    message: t('jobsPreview:errors.preview.message'),
                    description: t('jobsPreview:errors.preview.description'),
                });
            } finally {
                setProcessingFileId(null);
            }
        },
        [getFile, t]
    );

    const customRequest = useCallback(
        ({ file, onSuccess }) => {
            setSelectedFile(file);
            onFileSelect(file);
            onSuccess();
        },
        [onFileSelect]
    );

    const handleRemoveSelected = useCallback(() => {
        setSelectedFile(null);
        onFileSelect(null);
    }, [onFileSelect]);

    const handleDelete = useCallback(
        fileName => {
            setProcessingFileId(fileName); // Mark file as being processed
            deleteFile(fileName);
        },
        [deleteFile]
    );

    const itemRender = useCallback(
        (originNode, file) => {
            if (!['done', 'selected'].includes(file.status)) {
                return originNode;
            }
            return (
                <FileItem
                    file={file}
                    onPreview={handlePreview}
                    onDelete={file.status === 'selected' ? handleRemoveSelected : handleDelete}
                    jobStatus={jobStatus}
                />
            );
        },
        [handlePreview, handleRemoveSelected, handleDelete]
    );

    return (
        <div className={classes['upload-section']}>
            <Dragger
                {...props}
                fileList={fileList}
                customRequest={customRequest}
                itemRender={itemRender}
                beforeUpload={file => beforeUpload(file, fileTypes, null, t)}
                style={{
                    backgroundColor: colors.gray25,
                }}
            >
                <Title level={5} className={classes['upload-title']}>
                    {`${title} ( ${isRequired ? t('jobPreview:required') : t('jobPreview:optional')} )`}
                </Title>
                <p className={classes['upload-text']}>
                    {`${t('fileActions:drag-and-drop.textStart')} `} {title}
                    {`${t('fileActions:drag-and-drop.textEnd')}`}
                </p>
            </Dragger>
        </div>
    );
}

export default React.memo(DragAndDropButton);
